import { useEffect, useRef, useState } from "react";
import { Bell, Loader2 } from "lucide-react";
import { useAuthStore } from "@/store/auth";
import { useNotificationsStore } from "@/store/notifications";
import { formatDateTimeShort } from "@/lib/utils";

const POLL_INTERVAL_MS = 60_000;

export default function NotificationsBell() {
  const { isAuthenticated } = useAuthStore();
  const { items, unreadCount, loading, fetchUnreadCount, fetchItems, markRead } =
    useNotificationsStore();
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  // Poll unread count while authenticated
  useEffect(() => {
    if (!isAuthenticated) return;
    void fetchUnreadCount();
    const timer = setInterval(() => void fetchUnreadCount(), POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [isAuthenticated, fetchUnreadCount]);

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [open]);

  if (!isAuthenticated) return null;

  const handleToggle = () => {
    const next = !open;
    setOpen(next);
    if (next) void fetchItems();
  };

  return (
    <div className="relative" ref={panelRef} data-testid="notifications-bell">
      <button
        onClick={handleToggle}
        className="relative flex items-center p-1.5 rounded-lg hover:bg-gray-100 transition-colors text-text-secondary hover:text-text"
        aria-label="Уведомления"
        data-testid="notifications-bell-button"
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span
            className="absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-red-600 text-[10px] font-bold text-white"
            data-testid="notifications-unread-badge"
          >
            !
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-80 max-w-[calc(100vw-2rem)] rounded-xl border border-border bg-surface shadow-lg z-50"
          data-testid="notifications-panel"
        >
          <div className="px-4 py-3 border-b border-border">
            <p className="text-sm font-semibold">Уведомления</p>
          </div>
          <div className="max-h-96 overflow-y-auto overscroll-contain">
            {loading && items.length === 0 ? (
              <div className="flex justify-center py-6">
                <Loader2 className="h-5 w-5 animate-spin text-text-muted" />
              </div>
            ) : items.length === 0 ? (
              <p
                className="px-4 py-6 text-sm text-text-muted text-center"
                data-testid="notifications-empty"
              >
                Пока нет уведомлений
              </p>
            ) : (
              <ul className="divide-y divide-border">
                {items.map((item) => (
                  <li key={item.id}>
                    <button
                      onClick={() => void markRead(item.id)}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors flex gap-2"
                      data-testid={`notification-item-${item.id}`}
                    >
                      <span className="w-2 shrink-0 pt-1.5">
                        {!item.is_read && (
                          <span
                            className="block h-2 w-2 rounded-full bg-primary"
                            data-testid={`notification-unread-dot-${item.id}`}
                          />
                        )}
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm whitespace-pre-line break-words">
                          {item.text}
                        </span>
                        <span className="block text-xs text-text-muted mt-1">
                          {formatDateTimeShort(item.created_at)}
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
